import React, { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { useModal } from '../../../../hooks/modal';
import { useToast } from '../../../../hooks/toast';
import { AddWarning } from '../../../../modais/addWarning';
import { ChangeWarning } from '../../../../modais/changeWarning';
import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';

import { SortableWarningCard } from './components/SortableWarningCard';

import {
  AddButton,
  Container,
  ContainerInfo,
  Header,
  Info,
  MainGrid,
  NoDataContent,
  PageSubtitle,
  PageTitle,
  PhoneApp,
  PhoneAppLabel,
  PhoneBadge,
  PhoneCard,
  PhoneCardContent,
  PhoneCardTitle,
  PhoneEmpty,
  PhoneShell,
  SectionHeader,
  SectionLine,
  WarningsList,
} from './styles';

interface IWarningsProps {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  scheduled_for?: string | null;
}

const buildDateLabel = (warning: IWarningsProps): string => {
  if (warning.scheduled_for && new Date(warning.scheduled_for) > new Date()) {
    return `Agendado para ${new Date(warning.scheduled_for).toLocaleString(
      'pt-BR',
      { dateStyle: 'short', timeStyle: 'short' },
    )}`;
  }

  const edited =
    new Date(warning.updated_at).getTime() >
    new Date(warning.created_at).getTime() + 1000;

  const distance = formatDistanceToNow(new Date(warning.updated_at), {
    locale: ptBR,
  });

  return `${edited ? 'Editado' : 'Publicado'} há ${distance}`;
};

const Warnings: React.FC = () => {
  const [warnings, setWarnings] = useState<IWarningsProps[]>([]);
  const [loading, setLoading] = useState(true);

  const { addToast } = useToast();
  const { addModal, modals } = useModal();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      setWarnings(current => {
        const oldIndex = current.findIndex(item => item.id === active.id);
        const newIndex = current.findIndex(item => item.id === over.id);
        const reordered = arrayMove(current, oldIndex, newIndex);

        api
          .patch('news/reorder', { order: reordered.map(item => item.id) })
          .catch(() => {
            setWarnings(current);
            addToast({
              type: 'error',
              title: 'Erro ao reordenar avisos.',
            });
          });

        return reordered;
      });
    },
    [addToast],
  );

  const handleRemoveWarning = useCallback(
    async (warning_id: string) => {
      try {
        await api.delete(`news/${warning_id}`);

        setWarnings(warnings.filter(warning => warning.id !== warning_id));

        addToast({ type: 'success', title: 'Aviso removido com sucesso.' });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro na remoção do aviso',
          description:
            'Houve um erro na remoção do aviso. Caso o problema persista entre em contato com o administrador do sistema.',
        });
      }
    },
    [addToast, warnings],
  );

  useEffect(() => {
    setLoading(true);
    api
      .get('news')
      .then(response => setWarnings(response.data))
      .catch(err => console.log(err?.response?.status))
      .finally(() => {
        setLoading(false);
      });
  }, [modals]);

  const latestWarning = warnings[0];

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>Avisos</PageTitle>
            <PageSubtitle>
              Comunicados exibidos na tela inicial do app dos alunos
            </PageSubtitle>
          </div>
          <AddButton type="button" onClick={() => addModal(<AddWarning />)}>
            <Icon name="campaign" size={18} color={colors.onPrimary} />
            Novo aviso
          </AddButton>
        </Header>

        <ContainerInfo>
          <Info text="Os avisos ficam disponíveis na tela inicial de todos os alunos da academia gerenciada por você." />
          <Info text="Arraste os avisos pela lista abaixo para definir a ordem de exibição no app." />
        </ContainerInfo>

        <MainGrid>
          <div>
            <SectionHeader>
              <h3>
                {warnings.length === 1
                  ? '1 aviso cadastrado'
                  : `${warnings.length} avisos cadastrados`}
              </h3>
              <SectionLine />
            </SectionHeader>

            {warnings.length ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={warnings.map(warning => warning.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <WarningsList>
                    {warnings.map(warning => (
                      <SortableWarningCard
                        key={warning.id}
                        id={warning.id}
                        title={warning.title}
                        content={warning.content}
                        dateLabel={buildDateLabel(warning)}
                        functionRemoveButton={() =>
                          handleRemoveWarning(warning.id)
                        }
                        functionChangeButton={() =>
                          addModal(
                            <ChangeWarning
                              id={warning.id}
                              title={warning.title}
                              content={warning.content}
                            />,
                          )
                        }
                      />
                    ))}
                  </WarningsList>
                </SortableContext>
              </DndContext>
            ) : (
              <NoDataContent>
                <Icon
                  name="campaign"
                  size={28}
                  color={colors.onSurfaceVariant}
                />
                <span>Nenhum aviso cadastrado ainda.</span>
              </NoDataContent>
            )}
          </div>

          {/* <div>
            <SectionHeader>
              <h3>Pré-visualização</h3>
              <SectionLine />
            </SectionHeader>

            <PhoneShell>
              <PhoneApp>
                <PhoneAppLabel>Como aparece para o aluno</PhoneAppLabel>

                {latestWarning ? (
                  <PhoneCard>
                    <PhoneBadge>
                      <Icon name="campaign" size={11} />
                      Aviso
                    </PhoneBadge>
                    <PhoneCardTitle>{latestWarning.title}</PhoneCardTitle>
                    <PhoneCardContent>{latestWarning.content}</PhoneCardContent>
                  </PhoneCard>
                ) : (
                  <PhoneEmpty>Sem avisos para exibir</PhoneEmpty>
                )}
              </PhoneApp>
            </PhoneShell>
          </div> */}
        </MainGrid>
      </Container>
    </Painel>
  );
};

export { Warnings };
